// @flow

import WeakMap from "core-js/fn/weak-map";

import {
	Error,
	ignoreHandledErrors,
	isErrorAlike,
	fromErrorAlike,
} from "error";

import invariant from "invariant";

import url from "url";

import _ from "lodash";

import ko from "rgs-knockout";
import vb from "validation-builder";

import { TemporaryFileInfo } from "temporary-file-info";

import { ObservableBusy } from "observable-busy";

import { root as rootLogContext, defaultRejectDataGetters } from "log-context";
import type { LogContextInterface } from "log-context";

import { ValidationError } from "rgs-knockout-validation";
import page from 'page-common';

export class FileUploadError extends Error {
	name = "FileUploadError";
	code = "RGSRUUI-01000";
}

type FileUpload2Params = {
	file?: any,
	files?: any,
	doAfterTempUpload?: (files: TemporaryFileInfo) => any,

	addToBusy?: any,
	disable?: any,

	placeholder?: any,

	event?: Object,

	logContext?: LogContextInterface,

	mimeTypes: string[],
	filesFormat: string,

	TemporaryFileInfo: Class<any>,

	// jqFileupload options
	url: string,
	dataType: string,
	formAcceptCharset: string,

	/**
	 * By default, the file input field is replaced with a clone
	 * after each input field change event.
	 * This is required for iframe transport queues and allows change events to be fired
	 * for the same file selection, but can be disabled by setting this option to false.
	 * Default: true
	 */
	replaceFileInput: ?boolean,

	maxFileSize?: number,

	// TODO: research why native Blueimp 'destroy' event aint fires
	onFileRemove: ?(file: any) => void,

	// Callback to fire on file add, but before all component-native code
	onFileAddProlog: ?(e: Event, data: *) => void,

	multiple: ?boolean,

	// 'name' attribute, usefull for submits
	inputNameAttr: ?string
};

export class FileUpload2ViewModel {
	_params: FileUpload2Params;

	_disposeWhenComponentIsDisposed: Array<{ dispose(): void }>;

	_files: any;

	_disable: any;

	_nativeFileToFileModel: WeakMap;
	_fileModelToJqFileuploadData: WeakMap;
	_fileModelToRejectionReason: WeakMap;

	_busy: ObservableBusy;

	_logContext: () => LogContextInterface;

	constructor (params: FileUpload2Params) {
		this._logContext = () => ko.unwrap(params.logContext) || rootLogContext;

		this._params = params = _.defaults({}, params, {
			file: undefined,
			files: undefined,
			doAfterTempUpload: _.noop,

			addToBusy: undefined,
			disable: undefined,

			placeholder: undefined,
			mimeTypes: [],
			filesFormat: 'All',
			event: undefined,

			TemporaryFileInfo,

			// jqFileupload options
			url: "/api/file/saveTemporaryFile/",
			dataType: "json",
			formAcceptCharset: "utf-8",
			replaceFileInput: true,

			onFileRemove: null,
			onFileAddProlog: null,

			multiple: true,
			inputNameAttr: null
		});

		this._disposeWhenComponentIsDisposed = [];

		if (this._params.file) {
			const file = this._params.file;
			this._params.maxNumberOfFiles = 1;
			this._files = ko.pureComputed({
				read: () => {
					const f = file();
					return f ? [f] : [];
				},
				write: files => file(_.last(files))
			});
			this._disposeWhenComponentIsDisposed.push(this._files);
		} else if (params.files) {
			this._files = this._params.files;
		} else {
			throw new TypeError(
				"One of `file` or `files` component params must be specified"
			);
		}

		this._disable = ko.pureComputed(() => {
			return ko.unwrap(this._params.disable);
		});
		this._disposeWhenComponentIsDisposed.push(this._disable);

		this._nativeFileToFileModel = new WeakMap();
		this._fileModelToJqFileuploadData = new WeakMap();
		this._fileModelToRejectionReason = new WeakMap();

		this._busy = new ObservableBusy();
		this._disposeWhenComponentIsDisposed.push(this._busy);
	}

	get _valueObservable(): any {
		return this._params.file || this._params.files;
	}

	get accept(): string {
		return _.join(this._params.mimeTypes, ",");
	}

	get inputName(): string {
		return this._params.inputNameAttr;
	}

	get replaceFileInput(): boolean {
		return this._params.replaceFileInput;
	}

	_$fileupload() {
		return {
			files: this._files,
			removeFileClick: this._removeFileClick.bind(this),
			disable: this._disable,
			placeholder: this._params.placeholder,
			isBusy: this._busy.isBusy.bind(this._busy)
		};
	}

	_jqFileuploadOptions() {
		return _(this._params)
			.omit([
				"file",
				"files",
				"disable",
				"addToBusy",
				"TemporaryFileInfo"
			])
			.extend({
				autoUpload: true,
				singleFileUploads: true,

				disabled: this._disable(),

				add: this._add.bind(this),
				submit: this._submit.bind(this),
				done: this._done.bind(this),
				fail: this._fail.bind(this),
			})
			.mapValues(ko.unwrap)
			.value();
	}

	_getSubmitUrl () {
		const params = this._params;

		const query = _(params)
			.pick(["maxFileSize"])
			.mapValues(ko.unwrap)
			.pickBy()
			.value();

		const mappedUrl = url.format(
			_.extend(url.parse(params.url, true), {
				search: null,
				query,
			})
		);

		return `${mappedUrl}?allowFormats=${params.filesFormat}`;
	}

	_setIsModified () {
		const observable = this._valueObservable;

		if (!observable || !observable.isModified) {
			return;
		}

		observable.isModified(true);
	}

	_createFileModel(file: *) {
		const fileModel = new this._params.TemporaryFileInfo();

		fileModel.Size(file.size);
		fileModel.Name(file.name);

		this._nativeFileToFileModel.set(file, fileModel);
	}

	_pullFilesByMaxSize(files: Array<*>) {
		const maxFileSize = ko.unwrap(this._params.maxFileSize);

		if (!maxFileSize) {
			return;
		}

		const observable = this._valueObservable;

		const maxFileSizeValidator = vb.fileMaxSize(maxFileSize);

		_.each(files.slice(), file => {
			if (!file.size) {
				return;
			}

			const fileModel = this._nativeFileToFileModel.get(file);

			if (maxFileSizeValidator.validator(fileModel)) {
				return;
			}

			_.pull(files, file);

			const validationMessage = maxFileSizeValidator.message();

			this._fileModelToRejectionReason.set(
				fileModel,
				new ValidationError([
					{
						observable,
						message: validationMessage,
						value: fileModel,
					},
				])
			);
			fileModel.Upload.State("rejected");
		});
	}

	_add(e: Event, data: *) {
		this._params.onFileAddProlog
			&& this._params.onFileAddProlog(...arguments);

		this._setIsModified();

		_.each(data.files, file => {
			this._createFileModel(file);

			const fileModel = this._nativeFileToFileModel.get(file);

			this._logContextBeforeFileUploadStart(fileModel);

			const files = this._files.peek().slice();
			files.push(fileModel);
			this._files(files);
		});

		this._pullFilesByMaxSize(data.files);

		if (!data.files.length) {
			return;
		}

		data.process().done(() => {
			data.url = this._getSubmitUrl();
			console.log(data.url);

			const jqXHR = data.submit();
			this._addToBusy(jqXHR);
		});
	}

	_logContextBeforeFileUploadStart(fileModel: *) {
		const logContext = this._logContext();

		if (logContext && logContext.businessOperation) {
			logContext
				.suboperation(
					logContext.businessOperation,
					() => {
						// HACK, TODO: This should be handled outside of this component.
						// The operation function can be defined in this component,
						// but `dataGetters` and `businessOperation` should be provided by
						// component callee.
						// Validation thing below is terrible too. As well as promisification of a computed.
						return new Promise((resolve, reject) => {
							const uploadStateComputed = ko
								.computed(() => {
									const state = fileModel.Upload.State();
									const error = this._fileModelToRejectionReason.get(
										fileModel
									);
									const observable = this._valueObservable;
									const validatedFileModel = observable();
									const validationMessage = _.result(
										observable,
										"error"
									);

									ko.ignoreDependencies(() => {
										if (!state || state === "pending") {
											// noop
											// we must wait for upload to finish before settling this promise
										} else if (state === "rejected") {
											invariant(
												error,
												"Error must be defined if upload has been rejected"
											);
											error.handled = {
												type: "silently",
											};
											reject(error);

											uploadStateComputed
												&& uploadStateComputed.dispose();
										} else {
											invariant(
												state === "fulfilled",
												"Upload must have been fulfilled"
											);

											if (
												validationMessage
												&& validatedFileModel === fileModel
											) {
												// NOTE: `validatedFileModel` may differ from `fileModel` if
												// `value` params has changed during upload.
												// See test for part 2 of #RGSSUP-8212
												reject(
													new ValidationError([
														{
															observable,
															message: validationMessage,
															value: validatedFileModel,
														},
													])
												);
											} else {
												resolve(fileModel);
											}

											uploadStateComputed
												&& uploadStateComputed.dispose();
										}
									});
								})
								.extend({
									rateLimit: {
										timeout: 300,
										method: "notifyWhenChangesStop",
									},
								});
						});
					},
				{
					dataGetters: {
						start: () => ({
							FileModel: fileModel.toJS(),
						}),
						reject: defaultRejectDataGetters,
					},
				}
				)
				.catch(ignoreHandledErrors)
				.done();
		}
	}

	_addToBusy(task: Promise<void>) {
		this._busy.add(task);
		if (!this._params.addToBusy) {
			return;
		}
		this._params.addToBusy.add(task);
	}

	_submit(e: Event, data: *) {
		_.each(data.files, file => {
			const fileModel = this._nativeFileToFileModel.get(file);

			this._fileModelToJqFileuploadData.set(fileModel, data);
		});
	}

	_done (e: Event, data: *) {
		const {
			result: { Data },
		} = data;
		console.log(data);
		console.log(Data);
		const ONE_AT_A_TIME_MSG = "Only works with one by one file upload";
		invariant(_.keys(Data).length === 1, ONE_AT_A_TIME_MSG);
		invariant(data.files.length === 1, ONE_AT_A_TIME_MSG);

		_.each(data.files, file => {
			const fileModel = this._nativeFileToFileModel.get(file);

			const fileResponseDataID = _.head(_.keys(Data));

			const fileResponseData = Data[fileResponseDataID];

			fileModel.ID = fileResponseDataID;
			fileModel.fromJS(fileResponseData);
			fileModel.Upload.State("fulfilled");
		});

		this._params.doAfterTempUpload(this._files());
	}

	_fail(event: Event, data: Object) {
		console.log(data, event);
		console.log(this._files());
		//eslint-disable-next-line max-len
		const filesWithErrors = data.jqXHR && data.jqXHR.responseJSON && data.jqXHR.responseJSON.ErrorMessagesForClient;
		if (filesWithErrors) {
			//eslint-disable-next-line max-len
			console.log(this._files().filter((file: any) => !data.jqXHR.responseJSON.ErrorMessagesForClient.some((errorMessage) => {
				console.log(errorMessage, file.Name());
				return errorMessage === file.Name();
			})));
			//eslint-disable-next-line max-len
			this._files(this._files().filter((file: any) => !data.jqXHR.responseJSON.ErrorMessagesForClient.some((errorMessage) => errorMessage === file.Name())));
		}

		page.notifications.push({
			css: { 'notification-danger': true },
			//eslint-disable-next-line max-len
			message: `${data.jqXHR && data.jqXHR.responseJSON && data.jqXHR.responseJSON.Error}: ${filesWithErrors && filesWithErrors.join(', ')}`,
		});

		const error = (() => {
			const { errorThrown } = data;
			if (errorThrown) {
				if (isErrorAlike(errorThrown)) {
					const error = fromErrorAlike(errorThrown);
					error.fileupload = data;
					error.event = event;
					console.log(data);
					return error;
				} else {
					return new FileUploadError(errorThrown, {
						fileupload: data,
						event,
					});
				}
			}
			return new FileUploadError(
				"File upload failed for unknown reason",
				{
					fileupload: data,
					event,
				}
			);
		})();

		_.each(data.files, file => {
			const fileModel = this._nativeFileToFileModel.get(file);
			this._fileModelToRejectionReason.set(fileModel, error);
			fileModel.Upload.State("rejected");
		});
	}

	_removeFileClick(fileModel: *) {
		const jqFileuploadData = this._fileModelToJqFileuploadData.get(
			fileModel
		);
		if (jqFileuploadData) {
			jqFileuploadData.abort();
		}

		const files = this._files.peek().slice();
		_.pull(files, fileModel);
		this._files(files);

		this._params.onFileRemove && this._params.onFileRemove(fileModel);
	}

	dispose () {
		_.each(this._disposeWhenComponentIsDisposed, disposable => {
			disposable.dispose();
		});
	}
}
